import { useMemo, useState, useEffect } from 'react'
import { roundHalf, parseGid, booleanStringIsTruthy } from 'lib/helpers'
import ReactStars from 'react-rating-stars-component'
import { ProductProvider, flattenConnection } from '@shopify/hydrogen-react'
import { ProductForm } from 'components/Product/ProductForm'
import { ProductReviews } from 'components/Product/ProductReviews'
import { RelatedProducts } from 'components/Product/RelatedProducts'
import { ProductMarketingCards } from 'components/Product/ProductMarketingCards'
import { ProductMarketingOverlay } from 'components/Product/ProductMarketingOverlay'
import { ProductDetails } from 'components/Product/ProductDetails'
import { SizingGuide } from 'components/Product/SizingGuide'
import { AnimatePresence } from 'motion/react'
import { ProductGallery } from 'components/Product/ProductGallery'
import { urlForImage } from 'lib/sanity.image'
import { ProductTags } from './ProductTags'
import { ProductCountdown } from './ProductCountdown'

export const ProductPage = ({ product, shopifyProduct }) => {
  const [sizingOpen, setSizingOpen] = useState(false)
  const [activeColor, setActiveColor] = useState<string | null>(null)
  const [activeMarketingTab, setActiveMarketingTab] = useState<string | null>(
    null
  )
  const isPreorder = useMemo(
    () => booleanStringIsTruthy(shopifyProduct?.preorder?.value),
    [shopifyProduct]
  )
  const isBackorder = useMemo(
    () => booleanStringIsTruthy(shopifyProduct?.backorder?.value),
    [shopifyProduct]
  )

  const isSustainabilityHidden = useMemo(() => {
    return booleanStringIsTruthy(shopifyProduct?.hideSustainability?.value)
  }, [shopifyProduct])

  useEffect(() => {
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.track('ViewContent', {
          content_ids: [product.store.id],
          value: product.store.priceRange.minVariantPrice,
          content_name: product.store.title,
          content_type: 'product',
          currency: 'USD',
          content_category: product.store.productType,
        })
      })
  }, [product.store])

  const hasSizingCategory = useMemo(() => {
    return product?.details?.sizing?.categories?.some(
      (category) => category?.productType === shopifyProduct?.productType
    )
  }, [shopifyProduct, product.details])

  const marketingTabs = useMemo(
    () => [
      { label: 'Impact', type: 'impact', details: product.cause },
      ...(!isSustainabilityHidden
        ? [
            {
              label: product.details.factory.tabLabel,
              details: product.details.factory,
              type: 'factory',
            },
          ]
        : []),
      { label: 'Worn by you', type: 'wornByYou', details: product.wornByYou },
    ],
    [product]
  )

  const images = useMemo(() => {
    if (product.photos) {
      if (activeColor) {
        const colorOption = product.photos.find(
          (photo) => photo.forOption === activeColor
        )
        if (colorOption) return colorOption.photos
      }
      return product.photos[0]?.photos ?? []
    }
    return shopifyProduct?.images
      ? flattenConnection(shopifyProduct.images)
      : []
  }, [shopifyProduct, product.photos, activeColor])

  const onChangeColor = (color) => setActiveColor(color)

  const sizingText = useMemo(() => {
    const activeCategory = product.details?.sizing?.categories.find(
      (cat) => cat?.productType === shopifyProduct?.productType
    )
    return product.sizing || activeCategory?.description || null
  }, [product, shopifyProduct])

  if (!product || !shopifyProduct) return null

  return (
    <ProductProvider data={{ ...shopifyProduct, ...product }}>
      <AnimatePresence>
        {activeMarketingTab !== null && (
          <ProductMarketingOverlay
            tabs={marketingTabs}
            activeTab={activeMarketingTab}
            setActiveTab={setActiveMarketingTab}
          />
        )}
        {sizingOpen && (
          <SizingGuide
            {...product.details.sizing}
            type={shopifyProduct.productType}
            onClose={() => setSizingOpen(false)}
          />
        )}
      </AnimatePresence>
      <div className="md:pt-[100px]">
        <div className="flex flex-wrap justify-between md:gap-[120px]">
          <ProductGallery images={images} />
          <div className="sticky top-100 w-full self-start px-20 pb-20 text-midGray md:mr-40 md:max-w-[400px] md:px-0">
            <ProductTags
              isPreorder={isPreorder}
              isBackorder={isBackorder}
              discountAmount={shopifyProduct.discountAmount?.value}
              className="pb-10"
            />
            <h1 className="text-darkGray">{shopifyProduct.title}</h1>
            <a
              href="#reviews"
              className="flex cursor-pointer items-center gap-8 pb-40 pt-20 text-10 md:pb-20 md:pt-30"
            >
              <ReactStars
                count={5}
                size={12}
                value={roundHalf(product.reviews?.average)}
                color="rgba(94, 94, 94, 0.3)"
                activeColor="rgba(94, 94, 94, 1)"
                edit={false}
              />
              <div>{product.reviews?.count} Reviews</div>
            </a>
            <ProductCountdown
              discount={shopifyProduct.discountAmount?.value}
              date={shopifyProduct.shippingDate?.value}
            />
            <div className="flex flex-col-reverse gap-40 md:flex-col">
              <ProductDetails
                description={product.store.descriptionHtml}
                care={product.details.care}
                sustainability={
                  !isSustainabilityHidden
                    ? product.details.sustainability
                    : null
                }
                sizing={sizingText}
                openSizing={
                  hasSizingCategory ? () => setSizingOpen(true) : null
                }
              />
              <ProductForm
                onChangeColor={onChangeColor}
                shippingDate={shopifyProduct.shippingDate?.value}
                isPreorder={isPreorder}
                isBackorder={isBackorder}
                discount={shopifyProduct.discountAmount?.value}
                featuredImage={
                  images[0]?.src
                    ? images[0].src
                    : images[0]?.asset
                    ? urlForImage(images[0]).auto('format').width(200).url()
                    : ''
                }
              />
            </div>
            <ProductMarketingCards
              tabs={marketingTabs}
              activeTab={activeMarketingTab}
              setActiveTab={setActiveMarketingTab}
            />
          </div>
        </div>
        <ProductReviews
          title={product.store.title}
          image={images[0]}
          average={product.reviews?.average}
          count={product.reviews?.count}
          id={parseGid(shopifyProduct.id)}
          productId={product.reviews?.id}
        />
        <RelatedProducts id={product.store.gid} />
      </div>
    </ProductProvider>
  )
}
